import { useState } from 'react';

import { AddProfileImage } from '@components/add-profile-image';
import { createForm, useCreateFormStore } from '@components/form';
import { FormError } from '@components/form-error';
import { IconEdit } from '@components/icons/edit';
import { useSidebar } from '@components/sidebar';
import { hideSidebar, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useEditUser } from 'api/users/hooks';
import { User } from 'api/users/requests';

export const useEditTeamUserSidebar = () => {
  const sidebar = useSidebar(EditTeamUser, {
    variant: 'REGULAR',
  });
  return sidebar;
};

type UserFormData = {
  name: string;
  email: string;
  phone: string;
};

const Form = createForm<UserFormData>();

export const EditTeamUser = ({ user }: { user: User }) => {
  const { t } = useScopedTranslate(locale);

  const [picture, setPicture] = useState<File | null>();
  const [clearPicture, setClearPicture] = useState(false);
  const [emailError, setEmailError] = useState([] as string[]);

  const editMutation = useEditUser(user?.id ?? '', setEmailError, {
    onSuccess: hideSidebar,
  });

  const formStore = useCreateFormStore();
  const name = formStore.useFieldValue('name') as string;

  const handleSave = (data: UserFormData) => {
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('phone', data.phone);

    if (picture) {
      formData.append('picture', picture);
    } else if (clearPicture) {
      formData.append('picture', '');
    }
    return editMutation.mutate(formData);
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader icon={IconEdit} color="elementBlue100" title={t('editUser')} />
      <Sidebar.Content>
        <Form store={formStore} onSubmit={handleSave}>
          <Stack space={4} orientation="horizontal" align="center">
            <AddProfileImage
              name={name}
              initalPicture={user?.picture}
              picture={picture}
              setPicture={setPicture}
              clearPicture={clearPicture}
              setClearPicture={setClearPicture}
            />
            <Text font="semibold14" text={t('profileImage')} />
          </Stack>
          <Form.TextField
            id="name"
            label={t('core.fullName')}
            validators={['required']}
            defaultValue={user?.name ?? ''}
          />
          <Form.TextField
            id="email"
            label={t('core.email')}
            validators={['required', 'email']}
            defaultValue={user?.email}
          />
          {emailError && <FormError text={(emailError ?? []).join(' ')} />}
          <Form.PhoneField
            id="phone"
            label={t('core.phone')}
            validators={[]}
            defaultValue={user?.phone ?? ''}
          />
          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
              <Form.Submit text={t('updateUser')} variant="primary-blue" />
            </Form.Actions>
          </Sidebar.Footer>
        </Form>
      </Sidebar.Content>
    </Sidebar>
  );
};

export const locale = {
  en: {
    updateUser: 'Update user',
    editUser: 'Edit user',
    profileImage: 'Profile image',
  },
};
